/**
 * Centralized, typed environment access.
 *
 * Rules:
 * - This is the ONLY module that may read `process.env`.
 * - Required variables throw at import time in production builds.
 * - In dev / test, missing optionals fall back to safe defaults so the
 *   site is runnable straight from `git clone` without a `.env.local`.
 *
 * Anything exposed to the client must be prefixed `NEXT_PUBLIC_`. We do
 * NOT read server-only secrets here — this file is bundled.
 */

const isProd = process.env.NODE_ENV === "production";

/*
 * Turbopack (Next.js dev bundler) only statically inlines NEXT_PUBLIC_*
 * env vars when accessed via direct property syntax (e.g.
 * `process.env.NEXT_PUBLIC_FOO`).  Dynamic bracket access with a string
 * variable goes through the runtime polyfill which does not carry the
 * .env values.  We therefore capture each used variable here with a
 * direct property access so Turbopack / webpack can inline them, then
 * pass the resulting value into the helper functions below.
 */
const _RAW_ENV = {
  NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
  NEXT_PUBLIC_API_KEY: process.env.NEXT_PUBLIC_API_KEY,
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_FONT_FAMILY: process.env.NEXT_PUBLIC_FONT_FAMILY,
  NEXT_PUBLIC_USE_MOCK_API: process.env.NEXT_PUBLIC_USE_MOCK_API,
  NEXT_PUBLIC_USE_FIRESTORE: process.env.NEXT_PUBLIC_USE_FIRESTORE,
  NEXT_PUBLIC_ANALYTICS_PROVIDER: process.env.NEXT_PUBLIC_ANALYTICS_PROVIDER,
  NEXT_PUBLIC_ANALYTICS_DOMAIN: process.env.NEXT_PUBLIC_ANALYTICS_DOMAIN,
  NEXT_PUBLIC_GA4_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID,
  NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
  NEXT_PUBLIC_TIKTOK_PIXEL_ID: process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID,
  NEXT_PUBLIC_APP_STORE_URL_IOS: process.env.NEXT_PUBLIC_APP_STORE_URL_IOS,
  NEXT_PUBLIC_APP_STORE_URL_ANDROID: process.env.NEXT_PUBLIC_APP_STORE_URL_ANDROID,
  NEXT_PUBLIC_LANDING_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_LANDING_FIREBASE_API_KEY,
  NEXT_PUBLIC_LANDING_FIREBASE_AUTH_DOMAIN:
    process.env.NEXT_PUBLIC_LANDING_FIREBASE_AUTH_DOMAIN,
  NEXT_PUBLIC_LANDING_FIREBASE_PROJECT_ID:
    process.env.NEXT_PUBLIC_LANDING_FIREBASE_PROJECT_ID,
  NEXT_PUBLIC_LANDING_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_LANDING_FIREBASE_APP_ID,
  NEXT_PUBLIC_LANDING_FIREBASE_MESSAGING_SENDER_ID:
    process.env.NEXT_PUBLIC_LANDING_FIREBASE_MESSAGING_SENDER_ID,
  NEXT_PUBLIC_LANDING_FIRESTORE_COLLECTION_PREFIX:
    process.env.NEXT_PUBLIC_LANDING_FIRESTORE_COLLECTION_PREFIX,
} as const;

function readPublic(key: keyof typeof _RAW_ENV): string | undefined {
  const v = _RAW_ENV[key];
  if (v === undefined || v === "") return undefined;
  return v;
}

function required(key: keyof typeof _RAW_ENV, fallback?: string): string {
  const v = readPublic(key);
  if (v) return v;
  if (fallback !== undefined && !isProd) return fallback;
  throw new Error(
    `[env] Missing required environment variable \`${key}\`. ` +
      `See .env.example for the expected value.`,
  );
}

function optional(key: keyof typeof _RAW_ENV, fallback = ""): string {
  return readPublic(key) ?? fallback;
}

function bool(key: keyof typeof _RAW_ENV, fallback = false): boolean {
  const v = readPublic(key);
  if (v === undefined) return fallback;
  return v === "1" || v.toLowerCase() === "true";
}

export type AnalyticsProvider = "none" | "ga4" | "plausible" | "posthog";
export type MarketingFontFamily = "urbanist" | "codec-pro";

function analyticsProvider(): AnalyticsProvider {
  const v = optional("NEXT_PUBLIC_ANALYTICS_PROVIDER", "none").toLowerCase();
  if (v === "ga4" || v === "plausible" || v === "posthog") return v;
  return "none";
}

function marketingFontFamily(): MarketingFontFamily {
  const v = optional("NEXT_PUBLIC_FONT_FAMILY", "urbanist").trim().toLowerCase();
  if (v === "codec-pro" || v === "codec pro" || v === "codecpro" || v === "codec") {
    return "codec-pro";
  }
  return "urbanist";
}

export const env = {
  apiBaseUrl: required("NEXT_PUBLIC_API_BASE_URL", "https://api.runwaydev.es"),
  apiKey: optional("NEXT_PUBLIC_API_KEY"),
  siteUrl: required("NEXT_PUBLIC_SITE_URL", "https://runwayready.ai"),
  fontFamily: marketingFontFamily(),
  useMockApi: bool("NEXT_PUBLIC_USE_MOCK_API", false),
  useFirestore: bool("NEXT_PUBLIC_USE_FIRESTORE", false),
  appStoreUrlIos: optional(
    "NEXT_PUBLIC_APP_STORE_URL_IOS",
    "https://apps.apple.com/app/id6781571316",
  ),
  appStoreUrlAndroid: optional(
    "NEXT_PUBLIC_APP_STORE_URL_ANDROID",
    "https://play.google.com/store/apps/details?id=com.runwayready.runwayready_app",
  ),
  /** Landing Firestore project (homepage config getDoc). Not the main app project. */
  landingFirebase: {
    apiKey: optional("NEXT_PUBLIC_LANDING_FIREBASE_API_KEY"),
    authDomain: optional("NEXT_PUBLIC_LANDING_FIREBASE_AUTH_DOMAIN"),
    projectId: optional("NEXT_PUBLIC_LANDING_FIREBASE_PROJECT_ID"),
    appId: optional("NEXT_PUBLIC_LANDING_FIREBASE_APP_ID"),
    messagingSenderId: optional("NEXT_PUBLIC_LANDING_FIREBASE_MESSAGING_SENDER_ID"),
  },
  /** Collection prefix for `{prefix}_landing_config/active` (dev: rrai_landing_dev). */
  landingFirestoreCollectionPrefix: optional(
    "NEXT_PUBLIC_LANDING_FIRESTORE_COLLECTION_PREFIX",
    "rrai_landing_dev",
  ),
  analytics: {
    provider: analyticsProvider(),
    domain: optional("NEXT_PUBLIC_ANALYTICS_DOMAIN"),
    ga4MeasurementId: optional("NEXT_PUBLIC_GA4_MEASUREMENT_ID"),
    metaPixelId: optional("NEXT_PUBLIC_META_PIXEL_ID"),
    tiktokPixelId: optional("NEXT_PUBLIC_TIKTOK_PIXEL_ID"),
  },
  isProd,
} as const;

export type Env = typeof env;
